import React, { memo, useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { debounce } from 'lodash';

import { Spinner } from 'components';
import SelectionTickMark from 'components/form/SelectionTickMark';
import useScrollOffset from 'hooks/useScrollOffset';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useResponsive } from 'hooks/useResponsive';
import { useNotification } from 'hooks/useNotification';
import { useCandidate } from 'hooks/useCandidate';
import { useCandidateOnboarding } from 'hooks/useCandidateOnboarding';
import { MATCHING_PREFERENCE_STEPS } from 'lib/models/candidate';
import { Footer } from '../Footer/Footer.component';
import { SidebarMP } from '../SidebarMP/SidebarMP.component';
import MobileHeaderMp from '../MobileHeaderMp';

import styles from '../Onboarding.module.scss';

export interface InternshipTypeProps {
  /** Custom class name */
  className?: string;
  candidateSelectedInternships: Array<string> | null;
  onNextClick: (
    internshipSelected: Array<string>,
    isBackClicked?: boolean
  ) => void;
  isLoading: boolean;
  isBackLoading: boolean;
  internshipKinds: Array<string> | undefined;
  onStepSelect?: (step: MATCHING_PREFERENCE_STEPS) => void;
}

export const InternshipType: React.FC<InternshipTypeProps> = memo(
  ({
    className = '', // custom class name
    candidateSelectedInternships,
    onNextClick,
    isLoading,
    isBackLoading,
    internshipKinds,
    onStepSelect,
  }: InternshipTypeProps) => {
    const [t] = useTranslation('candidate-onboarding');
    const { isWindowScrolled } = useScrollOffset();
    const bottomRef = useRef<HTMLDivElement>(null);
    const screens = useResponsive();
    const notificationInstance = useNotification();
    const { updateCandidateInfo } = useCandidate();
    const { isValueEdited, setValueEdited, onBackClickMPMobile } =
      useCandidateOnboarding();

    const contentClassNames = cn(styles.container, className);

    const [selectedInternship, setSelectedInternship] = useState<Array<string>>(
      []
    );
    const [fullyScrolled, setFullyScrolled] = useState(false);

    useEffect(() => {
      if (candidateSelectedInternships) {
        setSelectedInternship(candidateSelectedInternships);
      }
    }, [candidateSelectedInternships]);

    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });

    const apiCallToUpdate = async (modifiedInternship: Array<string>) => {
      try {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            touchpointable_kinds: modifiedInternship,
          },
        });
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    const debouncedAPICall = React.useRef(
      debounce(async (modifiedInternship) => {
        await apiCallToUpdate(modifiedInternship);
      }, 3000)
    ).current;

    const onChevronClick = () => {
      if (isValueEdited) {
        onBackClickMPMobile();
      } else {
        onSubmit(false);
      }
    };

    const onSubmit = (isBackClicked?: boolean) => {
      setValueEdited(false);
      onNextClick(selectedInternship, isBackClicked);
    };

    const onInternshipCheckboxClick = (internshipType: string) => {
      setValueEdited(true);
      const index = selectedInternship?.findIndex(
        (type) => type === internshipType
      );
      let modifiedInternship = [];
      if (index === -1)
        modifiedInternship = [...selectedInternship, internshipType];
      else
        modifiedInternship =
          selectedInternship &&
          selectedInternship.filter((item) => item !== internshipType);

      /** For MP
       * Desktop: Autosave on every change
       * Mobile: Save on CTA click
       */
      setSelectedInternship(modifiedInternship);

      if (!screens.sm) {
        debouncedAPICall(modifiedInternship);
      }
    };

    const isReady = selectedInternship.length !== 0 ? true : false;

    if (!internshipKinds) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }

    return (
      <div>
        <MobileHeaderMp
          title={t('matching-preferences.internship-types')}
          onChevronClick={onChevronClick}
        />

        <div className={contentClassNames} data-testid="InternshipType">
          {onStepSelect && (
            <SidebarMP className={styles.sidebar} onStepSelect={onStepSelect} />
          )}

          <div className={cn(styles.content, styles['content-mp'])}>
            <p className={styles.setup}>{t('internship-kind.title')}</p>
            <div className={styles.checkboxes}>
              {internshipKinds?.map((kind) => (
                <SelectionTickMark
                  isTickSquare={true}
                  key={kind}
                  checked={selectedInternship?.some((id) => kind === id)}
                  onChange={() => onInternshipCheckboxClick(kind)}
                  className={cn(styles.selectionbox)}
                >
                  {kind}
                </SelectionTickMark>
              ))}
            </div>
            <div className={styles['bottom-ref']} ref={bottomRef}></div>
          </div>

          <Footer
            isBackLoading={isBackLoading}
            isLoading={isLoading}
            isReady={isReady}
            onSubmit={onSubmit}
            isFullyScrolled={fullyScrolled}
            isWindowScrolled={isWindowScrolled}
            isSkippable={true}
            isMatchingPreference={true}
          />
        </div>
      </div>
    );
  }
);

InternshipType.displayName = 'InternshipType';
